import { toast } from "react-toastify";
import { useEffect, useReducer, useState } from "react";
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { AuthData } from "../../auth/AuthWrapper";
import { useTranslation } from 'react-i18next';
import { IconShieldLock, IconCookie, IconAlertTriangle, IconMail, IconSend, IconCheck } from '@tabler/icons-react';
import { KamaModal } from '../structure/KamaModal';
import authService from '../../services/authService';

const getCookieConsent = () =>
    document.cookie.split(';').some(c => c.trim().startsWith('kama3d_cookie_consent='));

const setCookieConsent = (days) => {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `kama3d_cookie_consent=1;expires=${expires};path=/;SameSite=Strict`;
};

export const Login = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { login } = AuthData();
    const [errorMessage, setErrorMessage] = useState(null)
    const [rememberMe, setRememberMe] = useState(false)
    const [showModal, setShowModal] = useState(null);
    const [showCookieBanner, setShowCookieBanner] = useState(!getCookieConsent());

    // Aktivasyon Modal State'leri
    const [showActivationModal, setShowActivationModal] = useState(false);
    const [activationModalEmail, setActivationModalEmail] = useState("");
    const [isFromRegistration, setIsFromRegistration] = useState(false);
    const [resendingMail, setResendingMail] = useState(false);
    const [resendStatus, setResendStatus] = useState(null);

    const acceptCookies = () => {
        setCookieConsent(180);
        setShowCookieBanner(false);
    };

    const openModal = (type) => setShowModal(type);
    const closeModal = () => setShowModal(null);
    const [formData, setFormData] = useReducer((formData, newItem) => { setErrorMessage(""); return ({ ...formData, ...newItem }) }, { userName: localStorage.getItem('eposta') || "", password: "" })

    // Kayıt sonrası yönlendirme kontrolü
    useEffect(() => {
        if (location.state?.showActivationNotice) {
            const email = location.state.registeredEmail || "";
            if (email) {
                setFormData({ userName: email });
                setActivationModalEmail(email);
            }
            setIsFromRegistration(true);
            setShowActivationModal(true);
            window.history.replaceState({}, document.title);
        }
    }, [location.state]);

    const handleResendActivation = async () => {
        const emailToSend = activationModalEmail || formData.userName;
        if (!emailToSend) return;
        setResendingMail(true);
        setResendStatus(null);
        try {
            const res = await authService.resendActivation(emailToSend);
            if (res && res.success) {
                setResendStatus({
                    type: 'success',
                    message: t('mesajAktivasyonTekrarGonderildi')
                });
                toast.success(t('mesajAktivasyonTekrarGonderildi'));
            } else {
                setResendStatus({
                    type: 'error',
                    message: res?.message || t('mesajAktivasyonTekrarGonderHata')
                });
                toast.warning(res?.message || t('mesajAktivasyonTekrarGonderHata'));
            }
        } catch (err) {
            setResendStatus({
                type: 'error',
                message: t('mesajAktivasyonTekrarGonderHata')
            });
            toast.error(t('mesajAktivasyonTekrarGonderHata'));
        } finally {
            setResendingMail(false);
        }
    };

    const doLogin = async () => {
        try {
            const resp = await login(formData.userName, formData.password, rememberMe)
            if (resp.rizaOnay === false || resp.rizaOnay === null || resp.rizaOnay === 0) {
                navigate("/myaccount", { state: { forceRiza: true } })
            } else {
                navigate("/")
            }
        } catch (error) {
            if (error?.isNotActivated) {
                setIsFromRegistration(false);
                setActivationModalEmail(error.eposta || formData.userName);
                setShowActivationModal(true);
                return;
            }
            const msg = typeof error === 'string' ? error : error.message || t('mesajGirisHatali');
            toast.warning(msg)
            setErrorMessage(msg)
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            doLogin();
        }
    };

    const changeLang = async (lang) => {
        await i18n.changeLanguage(lang);
        localStorage.setItem('siteLang', lang);
    }

    const labelStyle = {
        fontSize: '11px', fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.5px', color: '#64748b', marginBottom: '6px', display: 'block',
    };

    return (
        <div style={{
            minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg, #1e2a38 0%, #2563a8 100%)',
            padding: '24px',
        }}>
            <div style={{
                display: 'flex', width: '100%', maxWidth: '820px',
                borderRadius: '20px', overflow: 'hidden',
                boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
            }}>
                {/* ── Sol Panel ── */}
                <div style={{
                    flex: 1, padding: '48px 40px', display: 'flex', flexDirection: 'column',
                    justifyContent: 'space-between', background: 'rgba(255,255,255,0.06)',
                    borderRight: '1px solid rgba(255,255,255,0.08)',
                }}>
                    <div>
                        <div style={{ marginBottom: '32px' }}>
                            <img src="Kama3DLogo.svg" alt="Kama 3D" style={{ width: 160 }} />
                        </div>
                        <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 12 }}>
                            {t('baslikAnaSayfa')}
                        </h4>
                        <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '14px', lineHeight: 1.7, margin: 0 }}>
                            {t('mesajLoginBilgi')}
                        </p>
                    </div>
                    <div>
                        <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: '12px', marginBottom: 8 }}>
                            © {new Date().getFullYear()} Kama 3D Doküman Merkezi
                        </div>
                        {i18n.language.startsWith('tr') && (
                            <div style={{ fontSize: '11px', display: 'flex', gap: '10px' }}>
                                <span style={{ color: 'rgba(255,255,255,0.4)', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => openModal('kvkk')}>
                                    KVKK
                                </span>
                                <span style={{ color: 'rgba(255,255,255,0.4)', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => openModal('cerez')}>
                                    Çerez Politikası
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Sağ Panel (Form) ── */}
                <div style={{ width: '360px', background: '#fff', padding: '48px 40px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>

                    {/* Dil + Başlık */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px' }}>
                        <h5 style={{ fontWeight: 700, color: '#1e293b', margin: 0 }}>{t('baslikGiris')}</h5>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <img src="flag-tr.png" alt="TR" onClick={() => changeLang('tr')}
                                style={{ width: 24, cursor: 'pointer', borderRadius: 3, opacity: i18n.language.startsWith('tr') ? 1 : 0.3, transition: 'opacity 0.2s' }} />
                            <img src="flag-en.png" alt="EN" onClick={() => changeLang('en')}
                                style={{ width: 24, cursor: 'pointer', borderRadius: 3, opacity: i18n.language.startsWith('en') ? 1 : 0.3, transition: 'opacity 0.2s' }} />
                        </div>
                    </div>

                    {/* E-posta */}
                    <div style={{ marginBottom: '16px' }}>
                        <label style={labelStyle}>{t('formUyelikEposta')}</label>
                        <input
                            value={formData.userName}
                            onChange={(e) => setFormData({ userName: e.target.value })}
                            className="form-control"
                            type="text"
                            placeholder="ornek@kama.com.tr"
                            onKeyDown={handleKeyDown}
                        />
                    </div>

                    {/* Şifre */}
                    <div style={{ marginBottom: '16px' }}>
                        <label style={labelStyle}>{t('formUyelikSifre')}</label>
                        <input
                            value={formData.password}
                            onChange={(e) => setFormData({ password: e.target.value })}
                            type="password"
                            className="form-control"
                            placeholder="••••••"
                            onKeyDown={handleKeyDown}
                        />
                    </div>

                    {/* Beni Hatırla */}
                    <div className="form-check mb-4">
                        <input className="form-check-input" type="checkbox" id="rememberMe"
                            checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
                        <label className="form-check-label small" htmlFor="rememberMe">
                            {t('beniHatirla')}
                        </label>
                    </div>

                    {/* Giriş Butonu */}
                    <button onClick={doLogin} className="btn btn-primary w-100 py-2 fw-bold mb-3">
                        {t('butonGiris')}
                    </button>

                    <div className="text-center mb-3">
                        <Link className="text-decoration-none small fw-bold" to={'/forgotpassword'} style={{ color: '#2563a8' }}>
                            {t('butonSifremiUnuttum')}
                        </Link>
                    </div>

                    <div className="position-relative text-center mb-3">
                        <hr style={{ borderColor: '#e2e8f0' }} />
                        <span className="position-absolute top-50 start-50 translate-middle bg-white px-3 text-muted small">
                            {i18n.language.startsWith('tr') ? 'veya' : 'or'}
                        </span>
                    </div>

                    <Link className="btn btn-outline-secondary w-100 py-2 fw-bold" to={'/addmember'}>
                        {t('butonYeniKullanici')}
                    </Link>

                    {errorMessage && (
                        <div className="alert alert-danger mt-3 mb-0 text-center small">
                            {errorMessage}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Cookie Consent Banner ── */}
            {showCookieBanner && (
                <div style={{
                    position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999,
                    background: 'rgba(15, 23, 42, 0.96)',
                    backdropFilter: 'blur(8px)',
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    padding: '14px 24px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    gap: '16px', flexWrap: 'wrap',
                }}>
                    <p style={{ color: 'rgba(255,255,255,0.8)', margin: 0, fontSize: '13px' }}>
                        {t('mesajCerezOnay')}{' '}
                        <span
                            onClick={() => openModal('cerez')}
                            style={{ color: '#58a4e2', cursor: 'pointer', textDecoration: 'underline' }}
                        >
                            {t('linkCerezPolitikasi')}
                        </span>
                    </p>
                    <button
                        onClick={acceptCookies}
                        className="btn btn-primary btn-sm px-4 fw-bold"
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        {t('butonTamam')}
                    </button>
                </div>
            )}

            <KamaModal
                show={showModal !== null}
                onHide={closeModal}
                title={showModal === 'kvkk' ? 'Kişisel Verilerin Korunması' : 'Çerez Politikası'}
                icon={showModal === 'kvkk' ? <IconShieldLock size={20} /> : <IconCookie size={20} />}
                size="lg"
                footer={
                    <button className="btn btn-primary fw-bold px-5" onClick={closeModal}>
                        {t('butonKapat')}
                    </button>
                }
            >
                <iframe
                    src={showModal === 'kvkk' ? '/kvkk.html' : '/cerez-politikasi.html'}
                    title={showModal === 'kvkk' ? 'Kişisel Verilerin Korunması' : 'Çerez Politikası'}
                    style={{ width: '100%', height: '65vh', border: 'none', display: 'block' }}
                />
            </KamaModal>

            {/* Aktivasyon Bilgilendirme ve Tekrar Gönderme Modalı */}
            <KamaModal
                show={showActivationModal}
                onHide={() => { setShowActivationModal(false); setResendStatus(null); }}
                title={t('baslikAktivasyonGerekli')}
                icon={<IconAlertTriangle size={20} color="#f59e0b" />}
                size="md"
                headerGradient="linear-gradient(135deg, #1e293b 0%, #b45309 100%)"
                footer={
                    <button
                        className="btn btn-primary fw-bold px-4"
                        onClick={() => { setShowActivationModal(false); setResendStatus(null); }}
                    >
                        {t('butonTamam')}
                    </button>
                }
            >
                <div className="p-4">
                    {isFromRegistration ? (
                        <div className="alert alert-success border-0 shadow-sm d-flex align-items-start gap-3 mb-3">
                            <IconCheck size={26} className="text-success flex-shrink-0 mt-1" />
                            <div>
                                <strong className="d-block mb-1 text-success">{t('mesajAktivasyonKayitSonrasi')}</strong>
                                <span className="small text-muted">{t('mesajAktivasyonGerekli')}</span>
                            </div>
                        </div>
                    ) : (
                        <div className="alert alert-warning border-0 shadow-sm d-flex align-items-start gap-3 mb-3">
                            <IconAlertTriangle size={26} className="text-warning flex-shrink-0 mt-1" />
                            <div>
                                <strong className="d-block mb-1 text-dark">{t('baslikAktivasyonGerekli')}</strong>
                                <span className="small text-muted">{t('mesajAktivasyonGerekli')}</span>
                            </div>
                        </div>
                    )}

                    <div className="card bg-light border-0 p-3 mb-3 rounded-3">
                        <h6 className="fw-bold mb-2 text-dark d-flex align-items-center gap-2">
                            <IconMail size={18} className="text-primary" />
                            {t('mesajAktivasyonMailUlasmadiBaslik')}
                        </h6>
                        <ul className="mb-0 small text-secondary ps-3" style={{ lineHeight: 1.8 }}>
                            <li>
                                {t('mesajAktivasyonKontrolEposta')}{' '}
                                <strong className="text-dark">{activationModalEmail || formData.userName}</strong>
                            </li>
                            <li dangerouslySetInnerHTML={{ __html: t('mesajAktivasyonKontrolSpam') }} />
                        </ul>
                    </div>

                    {resendStatus && (
                        <div className={`alert alert-${resendStatus.type === 'success' ? 'success' : 'danger'} border-0 small py-2 mb-3`}>
                            {resendStatus.message}
                        </div>
                    )}

                    <div className="mt-3">
                        <button
                            className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center gap-2 py-2 fw-semibold"
                            onClick={handleResendActivation}
                            disabled={resendingMail}
                        >
                            {resendingMail ? (
                                <>
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                    {t('mesajAktivasyonTekrarGonderiliyor')}
                                </>
                            ) : (
                                <>
                                    <IconSend size={18} />
                                    {t('mesajAktivasyonTekrarGonderBtn')}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </KamaModal>
        </div>
    )

}